import { createContext, useContext, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { PaletteCommand } from './commands';

type Source = RefObject<() => PaletteCommand[]>;
const Registry = createContext<Set<Source> | null>(null);

/** Collects the commands that components of a screen add to its ⌘K palette (share, history, a page's actions). */
export function PaletteRegistry({ children }: { children: ReactNode }) {
  const [sources] = useState(() => new Set<Source>());
  return <Registry.Provider value={sources}>{children}</Registry.Provider>;
}

/** Adds commands to the screen's palette while mounted; `get` is read at each opening, so it is never stale. */
export function usePaletteCommands(get: () => PaletteCommand[]): void {
  const sources = useContext(Registry);
  const ref = useRef(get);
  ref.current = get;
  useEffect(() => {
    if (!sources) return;
    sources.add(ref);
    return () => void sources.delete(ref);
  }, [sources]);
}

/** Reads the commands every mounted component added, in mount order. */
export function useRegisteredCommands(): () => PaletteCommand[] {
  const sources = useContext(Registry);
  return () => (sources ? [...sources].flatMap((source) => source.current()) : []);
}

const isEditable = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT');

/**
 * ⌘K / Ctrl+K anywhere on the screen but in a text field. The canvas handles its own (KEYMAP `palette`) and
 * marks the event handled, so the palette never opens twice.
 */
export function usePaletteShortcut(open: () => void): void {
  const ref = useRef(open);
  ref.current = open;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.shiftKey || !(e.metaKey || e.ctrlKey) || (e.code !== 'KeyK' && e.key.toLowerCase() !== 'k') || isEditable(e.target)) return;
      e.preventDefault();
      ref.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
